/**
 * Leviathan Web - Location Margins Component
 * Quasar Prime LLC
 * Location-specific product pricing margins with customer default support
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import { Plus, Trash2, Save, Loader2, DollarSign, Percent, X, Check, ArrowRight, Sparkles } from 'lucide-react';

import { API_URL } from '@/config/api';

interface Product {
  id: string;
  name: string;
  sku: string;
  pricePerUnit: number;
}

interface CustomerLocation {
  id: string;
  name: string;
  locationCode: string | null;
}

interface CustomerMargin {
  id: string;
  productId: string;
  customerLocationId: string | null;
  marginPercent: number | null;
  marginAmount: number | null;
  effectiveDate: string;
  expirationDate: string | null;
  isActive: boolean;
  isDefault: boolean;
  product: Product;
  customerLocation: CustomerLocation | null;
}

interface LocationMarginsProps {
  customerId: string;
  locationId: string;
}

export default function LocationMargins({ customerId, locationId }: LocationMarginsProps) {
  const { accessToken } = useAuth();

  const [margins, setMargins] = useState<CustomerMargin[]>([]);
  const [customerDefaults, setCustomerDefaults] = useState<CustomerMargin[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Form state with margin type selector
  const [marginType, setMarginType] = useState<'percent' | 'amount'>('percent');
  const [newMargin, setNewMargin] = useState({
    productId: '',
    marginPercent: '',
    marginAmount: '',
    effectiveDate: new Date().toISOString().split('T')[0] || '',
    expirationDate: '',
    notes: '',
  });

  useEffect(() => {
    fetchMargins();
    fetchCustomerDefaults();
    fetchProducts();
  }, [customerId, locationId]);

  const fetchMargins = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/api/customer-margins?customerId=${customerId}&customerLocationId=${locationId}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setMargins(data.margins || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch margins');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCustomerDefaults = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/customer-margins?customerId=${customerId}&isDefault=true`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCustomerDefaults(data.margins || []);
      }
    } catch (err) {
      console.error('Failed to fetch customer defaults:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory?isActive=true&limit=500`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data.items || []);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const handleAddMargin = async () => {
    // Validate product selection
    if (!newMargin.productId) {
      setError('Please select a product');
      return;
    }

    // Parse and validate margin value
    const marginPercent = marginType === 'percent' && newMargin.marginPercent.trim()
      ? parseFloat(newMargin.marginPercent)
      : null;
    const marginAmount = marginType === 'amount' && newMargin.marginAmount.trim()
      ? parseFloat(newMargin.marginAmount)
      : null;

    // Validate that at least one value is provided
    if (marginPercent === null && marginAmount === null) {
      setError('Please enter a margin value');
      return;
    }

    // Validate ranges
    if (marginPercent !== null && (isNaN(marginPercent) || marginPercent < -100 || marginPercent > 1000)) {
      setError('Margin percent must be between -100% and 1000%');
      return;
    }

    if (marginAmount !== null && isNaN(marginAmount)) {
      setError('Please enter a valid margin amount');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/customer-margins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          customerId,
          customerLocationId: locationId,
          productId: newMargin.productId,
          marginPercent: marginPercent,
          marginAmount: marginAmount,
          effectiveDate: newMargin.effectiveDate || undefined,
          expirationDate: newMargin.expirationDate || undefined,
          notes: newMargin.notes.trim() || undefined,
        }),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      // Get product name for success message
      const selectedProduct = products.find((p) => p.id === newMargin.productId);

      // Reset form
      setNewMargin({
        productId: '',
        marginPercent: '',
        marginAmount: '',
        effectiveDate: new Date().toISOString().split('T')[0] || '',
        expirationDate: '',
        notes: '',
      });
      setMarginType('percent');
      setShowAddForm(false);

      // Show success message
      setSuccessMessage(`Margin added for ${selectedProduct?.name || 'product'}`);
      setTimeout(() => setSuccessMessage(null), 3000);

      fetchMargins();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add margin');
    } finally {
      setIsSaving(false);
    }
  };

  const handleApplyDefault = async (defaultMargin: CustomerMargin) => {
    if (!confirm(`Apply customer default margin for ${defaultMargin.product.name} to this location?`)) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/customer-margins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          customerId,
          customerLocationId: locationId,
          productId: defaultMargin.productId,
          marginPercent: defaultMargin.marginPercent,
          marginAmount: defaultMargin.marginAmount,
          notes: `Applied from customer default`,
        }),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      setSuccessMessage(`Applied default margin for ${defaultMargin.product.name}`);
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchMargins();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply default');
    } finally {
      setIsSaving(false);
    }
  };

  const handleApplyAllDefaults = async () => {
    const availableDefaults = getAvailableDefaults();
    if (availableDefaults.length === 0) return;

    if (!confirm(`Apply all ${availableDefaults.length} customer default margins to this location?`)) {
      return;
    }

    setIsSaving(true);
    setError(null);

    let successCount = 0;
    let failCount = 0;

    for (const defaultMargin of availableDefaults) {
      try {
        const response = await fetch(`${API_URL}/api/customer-margins`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            customerId,
            customerLocationId: locationId,
            productId: defaultMargin.productId,
            marginPercent: defaultMargin.marginPercent,
            marginAmount: defaultMargin.marginAmount,
            notes: `Applied from customer default`,
          }),
        });

        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }

    if (successCount > 0) {
      setSuccessMessage(`Applied ${successCount} default margin${successCount !== 1 ? 's' : ''}`);
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchMargins();
    }
    if (failCount > 0) {
      setError(`Failed to apply ${failCount} margin${failCount !== 1 ? 's' : ''}`);
    }

    setIsSaving(false);
  };

  const handleToggleActive = async (margin: CustomerMargin) => {
    try {
      const response = await fetch(`${API_URL}/api/customer-margins/${margin.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ isActive: !margin.isActive }),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      fetchMargins();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update margin');
    }
  };

  const handleDeleteMargin = async (marginId: string) => {
    if (!confirm('Are you sure you want to delete this margin?')) return;

    try {
      const response = await fetch(`${API_URL}/api/customer-margins/${marginId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      fetchMargins();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete margin');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getAvailableProducts = () => {
    const usedProductIds = margins.filter((m) => m.isActive).map((m) => m.productId);
    return products.filter((p) => !usedProductIds.includes(p.id));
  };

  const getAvailableDefaults = () => {
    const usedProductIds = margins.map((m) => m.productId);
    return customerDefaults.filter(
      (def) => def.isActive && !usedProductIds.includes(def.productId)
    );
  };

  const formatMarginDisplay = (margin: CustomerMargin) => {
    if (margin.marginPercent !== null) {
      const value = Number(margin.marginPercent);
      return `${value >= 0 ? '+' : ''}${value}%`;
    }
    if (margin.marginAmount !== null) {
      const value = Number(margin.marginAmount);
      return `${value >= 0 ? '+' : ''}$${Math.abs(value).toFixed(2)}/gal`;
    }
    return '—';
  };

  const getMarginType = (margin: CustomerMargin): 'percent' | 'amount' => {
    return margin.marginPercent !== null ? 'percent' : 'amount';
  };

  const availableDefaults = getAvailableDefaults();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-amber" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-6" id="margins-section">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-success-green/10 border-2 border-success-green/50 p-4">
          <div className="flex items-center gap-3">
            <Check size={20} className="text-success-green" />
            <span className="text-success-green font-mono text-sm font-semibold">
              {successMessage}
            </span>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-error-red/10 border-2 border-error-red/50 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <span className="text-error-red text-xl">!</span>
              <div>
                <div className="text-error-red font-mono font-semibold text-sm mb-1">
                  Error
                </div>
                <div className="text-error-red font-mono text-sm">{error}</div>
              </div>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-error-red hover:text-error-red/80 p-1"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
            Location Margins
          </h3>
          <p className="text-text-tertiary font-mono text-sm mt-1">
            Pricing adjustments specific to this delivery location
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 bg-amber hover:bg-amber/90 text-background font-mono py-2 px-4 transition-all uppercase tracking-wider text-sm"
        >
          <Plus size={16} />
          Add Margin
        </button>
      </div>

      {/* Customer Defaults Available */}
      {availableDefaults.length > 0 && !showAddForm && (
        <div className="bg-steel/10 border border-steel/30 p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-steel" />
              <div>
                <div className="text-steel font-mono text-sm font-semibold">
                  Customer Defaults Available
                </div>
                <div className="text-text-secondary font-mono text-xs">
                  Quick-apply {availableDefaults.length} customer default margin{availableDefaults.length !== 1 ? 's' : ''} to this location
                </div>
              </div>
            </div>
            {availableDefaults.length > 1 && (
              <button
                onClick={handleApplyAllDefaults}
                disabled={isSaving}
                className="flex items-center gap-2 bg-steel/20 hover:bg-steel/30 text-steel border border-steel/50 font-mono py-1.5 px-3 text-xs uppercase tracking-wider transition-colors disabled:opacity-50"
              >
                Apply All
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {availableDefaults.map((def) => (
              <button
                key={def.id}
                onClick={() => handleApplyDefault(def)}
                disabled={isSaving}
                className="flex items-center justify-between p-3 bg-background border border-panel-border hover:border-steel/50 transition-colors text-left disabled:opacity-50"
              >
                <div>
                  <div className="text-text-primary font-mono font-semibold text-sm">
                    {def.product.name}
                  </div>
                  <div className="text-steel font-mono text-xs mt-0.5">
                    {formatMarginDisplay(def)}
                  </div>
                </div>
                <ArrowRight size={16} className="text-steel" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-background/50 border border-amber/30 p-6">
          <h4 className="text-text-primary font-mono font-semibold mb-4">Add New Margin</h4>

          <div className="space-y-6">
            {/* Product Selection */}
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Product <span className="text-error-red">*</span>
              </label>
              <select
                value={newMargin.productId}
                onChange={(e) => setNewMargin((prev) => ({ ...prev, productId: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-3 text-text-primary font-mono"
              >
                <option value="">Select Product...</option>
                {getAvailableProducts().map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} ({product.sku})
                  </option>
                ))}
              </select>
              {getAvailableProducts().length === 0 && (
                <p className="text-warning-amber font-mono text-xs mt-2">
                  All products already have margins configured for this location
                </p>
              )}
            </div>

            {/* Margin Type Selector */}
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-3">
                Margin Type <span className="text-error-red">*</span>
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setMarginType('percent');
                    setNewMargin((prev) => ({ ...prev, marginAmount: '' }));
                  }}
                  className={`p-4 border-2 transition-all text-left ${
                    marginType === 'percent'
                      ? 'border-amber bg-amber/10'
                      : 'border-panel-border bg-background hover:border-amber/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        marginType === 'percent' ? 'border-amber' : 'border-panel-border'
                      }`}
                    >
                      {marginType === 'percent' && (
                        <div className="w-2 h-2 rounded-full bg-amber" />
                      )}
                    </div>
                    <Percent size={16} className={marginType === 'percent' ? 'text-amber' : 'text-text-tertiary'} />
                    <span className="font-mono font-semibold text-text-primary">Percentage</span>
                  </div>
                  <div className="text-text-tertiary font-mono text-xs">
                    Add % markup to base price
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMarginType('amount');
                    setNewMargin((prev) => ({ ...prev, marginPercent: '' }));
                  }}
                  className={`p-4 border-2 transition-all text-left ${
                    marginType === 'amount'
                      ? 'border-amber bg-amber/10'
                      : 'border-panel-border bg-background hover:border-amber/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        marginType === 'amount' ? 'border-amber' : 'border-panel-border'
                      }`}
                    >
                      {marginType === 'amount' && (
                        <div className="w-2 h-2 rounded-full bg-amber" />
                      )}
                    </div>
                    <DollarSign size={16} className={marginType === 'amount' ? 'text-amber' : 'text-text-tertiary'} />
                    <span className="font-mono font-semibold text-text-primary">Fixed Amount</span>
                  </div>
                  <div className="text-text-tertiary font-mono text-xs">
                    Add fixed $ per gallon
                  </div>
                </button>
              </div>
            </div>

            {/* Margin Value Input */}
            {marginType === 'percent' ? (
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Margin Percentage (%) <span className="text-error-red">*</span>
                </label>
                <div className="relative max-w-xs">
                  <input
                    type="number"
                    step="0.1"
                    value={newMargin.marginPercent}
                    onChange={(e) =>
                      setNewMargin((prev) => ({ ...prev, marginPercent: e.target.value }))
                    }
                    className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-3 text-text-primary font-mono text-lg pr-12"
                    placeholder="15.5"
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-text-tertiary font-mono text-lg">
                    %
                  </div>
                </div>
                <p className="text-text-tertiary text-xs font-mono mt-2">
                  Positive = markup, Negative = discount
                </p>
              </div>
            ) : (
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Margin Amount ($ per gallon) <span className="text-error-red">*</span>
                </label>
                <div className="relative max-w-xs">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary font-mono text-lg">
                    $
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={newMargin.marginAmount}
                    onChange={(e) =>
                      setNewMargin((prev) => ({ ...prev, marginAmount: e.target.value }))
                    }
                    className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber pl-10 pr-4 py-3 text-text-primary font-mono text-lg"
                    placeholder="0.25"
                  />
                </div>
                <p className="text-text-tertiary text-xs font-mono mt-2">
                  Positive = markup, Negative = discount
                </p>
              </div>
            )}

            {/* Date Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Effective Date
                </label>
                <input
                  type="date"
                  value={newMargin.effectiveDate}
                  onChange={(e) =>
                    setNewMargin((prev) => ({ ...prev, effectiveDate: e.target.value }))
                  }
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
                />
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Expiration Date <span className="text-text-tertiary">(optional)</span>
                </label>
                <input
                  type="date"
                  value={newMargin.expirationDate}
                  onChange={(e) =>
                    setNewMargin((prev) => ({ ...prev, expirationDate: e.target.value }))
                  }
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Notes <span className="text-text-tertiary">(optional)</span>
              </label>
              <textarea
                value={newMargin.notes}
                onChange={(e) => setNewMargin((prev) => ({ ...prev, notes: e.target.value }))}
                rows={2}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono resize-none"
                placeholder="Any notes about this margin..."
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-4 pt-4 border-t border-panel-border">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setError(null);
                }}
                className="bg-panel-dark border border-panel-border hover:border-amber/50 text-text-secondary font-mono py-2 px-6 transition-all uppercase tracking-wider text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddMargin}
                disabled={isSaving || !newMargin.productId}
                className="flex items-center gap-2 bg-amber hover:bg-amber/90 text-background font-mono py-2 px-6 transition-all uppercase tracking-wider text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Margin
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Margins List */}
      {margins.length === 0 ? (
        <div className="text-center py-12 bg-background/50 border border-panel-border">
          <DollarSign size={32} className="mx-auto mb-3 text-text-tertiary" />
          <div className="text-text-tertiary font-mono text-sm mb-3">
            No location-specific margins configured
          </div>
          <p className="text-text-tertiary font-mono text-xs">
            {availableDefaults.length > 0
              ? 'Apply customer defaults above or add custom margins for this location'
              : 'Add custom margins to adjust product pricing for this location'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-panel-border">
              <tr>
                <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">
                  Product
                </th>
                <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">
                  Type
                </th>
                <th className="text-right px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">
                  Margin
                </th>
                <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">
                  Effective
                </th>
                <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">
                  Status
                </th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {margins.map((margin) => {
                const type = getMarginType(margin);
                const value = type === 'percent' ? margin.marginPercent : margin.marginAmount;
                const isPositive = value !== null && Number(value) >= 0;

                return (
                  <tr key={margin.id} className="border-b border-panel-border hover:bg-background/30">
                    <td className="px-4 py-3">
                      <div className="text-text-primary font-mono">{margin.product.name}</div>
                      <div className="text-text-tertiary font-mono text-xs">{margin.product.sku}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-text-secondary font-mono text-sm">
                        {type === 'percent' ? (
                          <Percent size={14} />
                        ) : (
                          <DollarSign size={14} />
                        )}
                        {type === 'percent' ? 'Percent' : 'Fixed'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className={`font-mono font-semibold ${
                          isPositive ? 'text-success-green' : 'text-error-red'
                        }`}
                      >
                        {formatMarginDisplay(margin)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-text-secondary font-mono text-sm">
                        {formatDate(margin.effectiveDate)}
                      </div>
                      {margin.expirationDate && (
                        <div className="text-text-tertiary font-mono text-xs">
                          Expires: {formatDate(margin.expirationDate)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleActive(margin)}
                        className={`px-3 py-1 text-xs font-mono border transition-colors ${
                          margin.isActive
                            ? 'bg-success-green/20 text-success-green border-success-green hover:bg-success-green/30'
                            : 'bg-gray-500/20 text-gray-400 border-gray-500 hover:bg-gray-500/30'
                        }`}
                      >
                        {margin.isActive ? 'ACTIVE' : 'INACTIVE'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDeleteMargin(margin.id)}
                        className="text-error-red hover:text-error-red/80 p-1 transition-colors"
                        title="Delete margin"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Info Note */}
      <div className="bg-steel/10 border border-steel/30 p-4">
        <p className="text-text-secondary font-mono text-sm">
          <strong className="text-steel">Note:</strong> Location margins override customer defaults.
          Margins are applied to base product prices during order creation.
        </p>
      </div>
    </div>
  );
}
